/**
 * Reactive variables to bridge sort/filter sheet open requests between
 * SquadPlanningView (buttons) and MobileCardsViewContainer (sheet owner).
 *
 * MobileCardsViewContainer registers its openers via the reactive vars.
 * SquadPlanningView calls the request functions on button press.
 * @module features/squad-planning/stores/mobileToolbarStore
 */

import { makeVar } from "@apollo/client";

/** Holds the registered sort opener callback (set by MobileCardsViewContainer). */
export const sortSheetOpenerVar = makeVar<(() => void) | null>(null);

/** Holds the registered filter opener callback (set by MobileCardsViewContainer). */
export const filterSheetOpenerVar = makeVar<(() => void) | null>(null);

/**
 * Open request queued while no sort opener is registered (SE-5591).
 * MobileCardsViewContainer registers its openers only after its data settles,
 * so a toolbar tap during the load window used to be a silent no-op. The
 * request functions below queue the intent here instead; the owner flushes it
 * via useRegisterSheetOpener immediately after registering.
 */
export const pendingSortSheetRequestVar = makeVar(false);

/** Open request queued while no filter opener is registered (SE-5591). */
export const pendingFilterSheetRequestVar = makeVar(false);

/** Holds the active sort label to display on the Sort button (e.g. "GPR", "Age"). */
export const activeSortLabelVar = makeVar<string | null>(null);

/** Holds the active sort direction ("asc" or "desc") for the Sort button icon. */
export const activeSortDirVar = makeVar<"asc" | "desc">("desc");

/** Holds the active filter count for the Filters button badge. */
export const activeFilterCountVar = makeVar(0);

/** Holds the scenario action count for the Actions button badge. */
export const scenarioActionCountVar = makeVar(0);

/** Whether the transitions-only filter is active (shared between PlayerPanel and MobileToolbar). */
export const transitionsOnlyVar = makeVar(false);

// --- Applied filter state (persists across Cards/Map tab switches) ---

/** Applied position filter IDs. */
export const appliedPositionIdsVar = makeVar<readonly string[]>([]);
/** Applied age range filter. */
export const appliedAgeRangeVar = makeVar<[number, number]>([16, 40]);
/** Applied GPR range filter. */
export const appliedGprRangeVar = makeVar<[number, number]>([0, 100]);
/** Applied contract months range filter. */
export const appliedContractRangeVar = makeVar<[number, number]>([0, 60]);

/** Pending sign player data returned from /add-to-list?mode=sign route. */
export interface PendingSignPlayer {
  readonly id: string;
  readonly displayName: string;
  readonly teamName: string | null;
  readonly positions: string;
  readonly age: number | null;
  readonly gprScore: number | null;
  readonly nationalityName: string | null;
}

/** Holds pending sign player data for the sign flow return channel. */
export const pendingSignPlayerVar = makeVar<PendingSignPlayer | null>(null);

/**
 * Triggers the sort sheet open via the registered callback, or queues the
 * request when no opener is registered yet (SE-5591: never drop the tap).
 */
export function requestMobileSortSheet(): void {
  const opener = sortSheetOpenerVar();
  if (opener != null) {
    opener();
  } else {
    pendingSortSheetRequestVar(true);
  }
}

/**
 * Triggers the filter sheet open via the registered callback, or queues the
 * request when no opener is registered yet (SE-5591: never drop the tap).
 */
export function requestMobileFilterSheet(): void {
  const opener = filterSheetOpenerVar();
  if (opener != null) {
    opener();
  } else {
    pendingFilterSheetRequestVar(true);
  }
}
